import {useEffect, useMemo, useReducer, useState} from "react";
import {deleteEncounterAnnotation, getEncounterAnnotations, saveEncounterAnnotation} from "@/api/EncounterApi.ts";
import {useRealtimeChannel} from "@/api/realtime/RealtimeChannelHooks.ts";
import type {EncounterAnnotation, EncounterAnnotationEvent} from "@/encounter/model/EncounterAnnotation.ts";

type Action = {type: "replace"; annotations: EncounterAnnotation[]}
    | {type: "event"; event: EncounterAnnotationEvent};

function reducer(state: ReadonlyMap<string, EncounterAnnotation>, action: Action): ReadonlyMap<string, EncounterAnnotation> {
    if (action.type === "replace") return new Map(action.annotations.map(annotation => [annotation.id, annotation]));
    const next = new Map(state);
    if (action.event.upserted) next.set(action.event.upserted.id, action.event.upserted);
    if (action.event.removedId) next.delete(action.event.removedId);
    return next;
}

export function useEncounterAnnotations(encounterId: string) {
    const [byId, dispatch] = useReducer(reducer, new Map<string, EncounterAnnotation>());
    const [error, setError] = useState(false);
    const {subscribe} = useRealtimeChannel<EncounterAnnotationEvent>(`/encounters/${encounterId}/annotations`);

    useEffect(() => {
        let active = true;
        let loaded = false;
        const buffered: EncounterAnnotationEvent[] = [];
        dispatch({type: "replace", annotations: []});
        const unsubscribe = subscribe(event => {
            if (!active) return;
            if (loaded) dispatch({type: "event", event});
            else buffered.push(event);
        });
        void getEncounterAnnotations(encounterId).then(annotations => {
            if (!active) return;
            dispatch({type: "replace", annotations});
            buffered.forEach(event => dispatch({type: "event", event}));
            loaded = true;
            setError(false);
        }).catch(() => {
            if (active) setError(true);
        });
        return () => {active = false; unsubscribe();};
    }, [encounterId, subscribe]);

    const annotations = useMemo(() => Array.from(byId.values()), [byId]);
    const save = async (annotation: EncounterAnnotation) => {
        const saved = await saveEncounterAnnotation(encounterId, annotation);
        dispatch({type: "event", event: {upserted: saved}});
        return saved;
    };
    const remove = async (id: string) => {
        await deleteEncounterAnnotation(encounterId, id);
        dispatch({type: "event", event: {removedId: id}});
    };
    return {annotations, save, remove, error};
}
